import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, BookOpen, Download, Sparkles } from 'lucide-react';
import { trackPurchase, PRODUCT_DATA } from '@/lib/metaPixel';

const PaymentSuccess: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Track purchase event
    try {
      trackPurchase({
        value: PRODUCT_DATA.value,
        currency: PRODUCT_DATA.currency,
        content_ids: PRODUCT_DATA.content_ids,
        content_type: PRODUCT_DATA.content_type,
        content_name: PRODUCT_DATA.content_name,
      });
    } catch (e) {
      // Ignore tracking errors
    }
  }, []);

  const handleViewNotes = () => {
    navigate('/dashboard');
  };

  return (
    <>
      <Helmet>
        <title>Payment Successful - TOPPERS BUDDY</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-green-50 via-background to-blue-50 p-4">
        <Card className="w-full max-w-md border-green-200 shadow-xl overflow-hidden">
          {/* Confetti animation effect */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-4 left-1/4 w-2 h-2 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
            <div className="absolute -top-4 left-1/2 w-2 h-2 bg-green-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
            <div className="absolute -top-4 left-3/4 w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
          </div>

          <CardHeader className="text-center pb-4 bg-gradient-to-b from-green-50 to-transparent">
            <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center rounded-full bg-green-100 ring-4 ring-green-200 animate-fade-up">
              <CheckCircle className="h-14 w-14 text-green-600" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-2">
              <Sparkles className="h-5 w-5 text-yellow-500 animate-pulse" />
              <CardTitle className="text-2xl font-bold text-green-700">Payment Successful!</CardTitle>
              <Sparkles className="h-5 w-5 text-yellow-500 animate-pulse" />
            </div>
            <CardDescription className="text-base mt-2 text-foreground/80">
              Congratulations! Your Class 10 Science Notes are now ready.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="bg-green-50 rounded-lg p-4 border border-green-100">
              <p className="text-sm text-green-800 font-medium text-center">
                🎉 You now have lifetime access to all Toppers Short Notes!
              </p>
            </div>

            <div className="bg-muted/50 rounded-lg p-4 text-sm">
              <p className="font-medium mb-2 text-foreground">What you've unlocked:</p>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Complete Science Notes (All Chapters)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Toppers Tips & Key Formulas
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Instant PDF Downloads
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Lifetime Access
                </li>
              </ul>
            </div>

            <Button 
              onClick={handleViewNotes} 
              className="w-full h-14 text-lg gap-2 bg-green-600 hover:bg-green-700" 
              size="lg"
            >
              <BookOpen className="h-5 w-5" />
              View Your Notes Now
            </Button>

            <Button 
              onClick={handleViewNotes} 
              variant="outline" 
              className="w-full h-12 text-base gap-2 border-green-200 text-green-700 hover:bg-green-50" 
              size="lg"
            >
              <Download className="h-5 w-5" />
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default PaymentSuccess;
